import { DEFAULT_ARRIVAL_TIME, DEFAULT_DEPARTURE_TIME, toStayDateTime } from "../../utils/stayWindow";

/* A booking has an assigned guest house/room from the moment it's confirmed
   onward — including once the guest has checked in, checked out, or was
   marked a no-show — so anything that displays "which room/GH was this
   booking assigned to" should key off this set, not just "Approved". */
export const STATUSES_WITH_ASSIGNED_ROOM = [
  "Approved",
  "Partially Confirmed",
  "Checked In",
  "Checked Out",
  "No Show",
];

/* Narrower than the above: statuses where the room is *currently* occupied
   (still held, not yet released). Checked Out and No Show free the room —
   use this set for vacancy/double-booking checks, not the display set above. */
export const STATUSES_HOLDING_ROOM = [
  "Approved",
  "Partially Confirmed",
  "Checked In",
];

/* map booking code → JSON key  (L101→LD-101, F101→FG-201, S101→SSB-301, U101→UGH-401) */
export function toJsonKey(code) {
  if (!code) return null;
  const n = parseInt(code.slice(1), 10);
  if (code.startsWith("L")) return `LD-${n}`;
  if (code.startsWith("F")) return `FG-${n + 100}`;
  if (code.startsWith("S")) return `SSB-${n + 200}`;
  if (code.startsWith("U")) return `UGH-${n + 300}`;
  return null;
}

/* "LDE-101" when the guest house has a persisted short code, else the bare
   room number — matches the fallback the backend PDF/Excel reports use
   (guesthouses/reports.py, guesthouses/occupancy_list.py). Never invent a
   code from the name; an unconfigured guest house just shows its room
   number until an admin sets short_name in Guest House settings. */
export function roomLabel(roomNumber, shortName) {
  return shortName ? `${shortName}-${roomNumber}` : (roomNumber || "");
}

/* The two halves of the admin date format, "dd/mm/yyyy" and "hh:mm AM/PM",
   both zero-padded so a column of them lines up on the same widths. Built by
   hand rather than through toLocaleDateString/toLocaleTimeString: those
   depend on whatever ICU data sits behind a locale name, which pads the
   12-hour field inconsistently between browsers and renders en-IN's am/pm in
   lowercase. Everything the admin UI shows goes through these two. */
const ddmmyyyy = (d) =>
  [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join("/");

const hhmmA = (hours, minutes) => {
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d + "T00:00:00");
  return Number.isNaN(date.getTime()) ? "—" : ddmmyyyy(date);
};

/* fmt is the same formatting as fmtDate, kept as a separate export since
   both names are used across the admin components. */
export const fmt = fmtDate;

/* A Date's own calendar day as "YYYY-MM-DD" — deliberately not
   toISOString().slice(0, 10), which is the UTC day and so names yesterday
   until 05:30 in IST. Booking dates (checkIn/checkOut) are already plain
   local date strings, so anything compared against them has to be derived
   this way to agree with them — and with what fmtDate/fmtDateTime display. */
export const localDateKey = (d) =>
  [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");

/* Today's local date, or `offset` days either side of it. */
export const dayStr = (offset = 0) => {
  const d = new Date();
  if (offset) d.setDate(d.getDate() + offset);
  return localDateKey(d);
};

export const todayStr = () => dayStr(0);

/* The local calendar day of a full ISO timestamp (actual check-in/checkout),
   matching the date fmtDateTime renders for the same value. */
export const dateKeyOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : localDateKey(d);
};

/* Check-in opens on the arrival date and never closes again — a guest who
   turns up on day two still checks in. Mirrors the server's
   CHECKIN_TOO_EARLY guard (bookings/views.py) so the front desk sees the
   warning on the click rather than after the round trip; the server stays
   the authority, since the browser clock is the user's own.
   The date is each room's own (effectiveArrival): one room on an indent may
   be moved ahead of the booking's arrival. Returns the rooms among
   `assignments` that are not checked in yet and whose arrival is still
   ahead — empty when check-in may go ahead. */
export const checkinTooEarly = (booking, assignments) => {
  const today = todayStr();
  return (assignments || []).filter((a) => {
    if (a.actualCheckin) return false;
    const arrival = a.effectiveArrival || booking?.checkIn;
    return Boolean(arrival) && today < arrival;
  });
};

export const checkinTooEarlyMsg = (booking, early) =>
  `Check-in opens on the arrival date — room ` +
  early
    .map((a) => `${a.roomNumber} (${fmtDate(a.effectiveArrival || booking?.checkIn)})`)
    .join(", ") +
  ` cannot be checked in yet. To check the guest in today, reschedule ` +
  `the booking or change the room's arrival first.`;

/* arrivalTime/departureTime come back as plain "HH:MM:SS" strings (no date
   part), so they can't go through fmtDateTime — parse just the time. */
export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return "";
  return hhmmA(hour, Number(m) || 0);
};

/* Scheduled arrival/departure date plus its "HH:MM:SS" time, e.g.
   "17/08/2026, 03:00 PM" — falls back to just the date when no time is set. */
export function fmtDateAndTime(date, time) {
  const t = fmtTime(time);
  return t ? `${fmtDate(date)}, ${t}` : fmtDate(date);
}

/* A booking's arrival — the one the Arrival column shows (bookingArrival
   below, so a room moved onto its own window sorts by that) — as a naive
   local ISO datetime, "2026-08-17T15:00:00". Lexicographic order on this
   string is chronological order, so it compares with < / > and never
   allocates a Date. A row whose time was never recorded sorts as if it
   arrived at noon — the same fallback the server and the availability
   queries already use — so it can land between an 11:00 and a 13:00 arrival
   while displaying only a date. */
export const arrivalKey = (b) => bookingArrival(b).key;

/* toStayDateTime returns "" when there's no date at all, which would lead the
   list; NO_ARRIVAL sorts after every digit instead, so an incomplete row
   lands at the end where it belongs. */
const NO_ARRIVAL = "\uffff";

/* Earliest arrival first. Ties break on id so the order is identical on every
   reload regardless of what order the API returned the page in. */
export function compareByArrival(a, b) {
  const ka = arrivalKey(a) || NO_ARRIVAL;
  const kb = arrivalKey(b) || NO_ARRIVAL;
  if (ka !== kb) return ka < kb ? -1 : 1;
  return (a?.id ?? 0) - (b?.id ?? 0);
}

/* A Date's own local time as "HH:MM:SS" — the time half of localDateKey. */
const localTimeKey = (d) =>
  [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");

/* When a booking's stay ends, for the tables' Departure column. The booking's
   own checkOut/departureTime is what was booked and never moves: Change
   Departure writes to the room instead, and a guest can simply leave on
   another day. So each room ends at its actual checkout once it has one, and
   otherwise at its planned departure (the room's own after Change Departure,
   else the booking's), and the booking departs when its last room does.
   No-show rooms never stayed, and rooms only count while the booking's status
   holds them — a Cancelled booking keeps its assignment rows as history. With
   no room to go on it is the booked departure.

   `changed` flags a value that differs from the booked one. A planned
   departure counts on any difference, date or time, since only Change
   Departure moves it. An actual checkout counts only on a different day: real
   checkouts rarely land on the booked minute, and flagging every one would
   drown out the changes that matter. `original` is the booked departure,
   formatted for display. */
export function bookingDeparture(booking) {
  const b = booking || {};
  const booked = { date: b.checkOut || "", time: b.departureTime || "" };
  const bookedKey = toStayDateTime(booked.date, booked.time, DEFAULT_DEPARTURE_TIME);
  const original = fmtDateAndTime(booked.date, booked.time);

  let latest = null;
  if (STATUSES_WITH_ASSIGNED_ROOM.includes(b.status)) {
    for (const a of b.roomAssignments || []) {
      if (a.isNoShow) continue;
      let end;
      if (a.actualCheckout) {
        const d = new Date(a.actualCheckout);
        if (Number.isNaN(d.getTime())) continue;
        end = { date: localDateKey(d), time: localTimeKey(d), actual: true };
      } else {
        end = {
          date: a.effectiveDeparture || booked.date,
          time: a.effectiveDepartureTime || booked.time,
          actual: false,
        };
      }
      end.key = toStayDateTime(end.date, end.time, DEFAULT_DEPARTURE_TIME);
      if (end.key && (!latest || end.key > latest.key)) latest = end;
    }
  }

  if (!latest) return { ...booked, key: bookedKey, changed: false, original };
  const changed = latest.actual ? latest.date !== booked.date : latest.key !== bookedKey;
  return { date: latest.date, time: latest.time, key: latest.key, changed, original };
}

/* When a booking's stay starts, for the tables' Arrival column — the mirror
   of bookingDeparture above. The booking's own checkIn/arrivalTime is what
   was booked; a room confirmed or moved onto its own window (Change Stay
   Window, a staggered confirm) carries its start on the assignment instead.
   So each room starts at its actual check-in once it has one, otherwise at
   its planned arrival (the room's own, else the booking's), and the booking
   arrives when its first room does. `planned: true` skips actual check-ins,
   for the places that want the expected window (Copy Details, the printed
   form). `changed` and `original` work exactly as for departures. */
export function bookingArrival(booking, { planned = false } = {}) {
  const b = booking || {};
  const booked = { date: b.checkIn || "", time: b.arrivalTime || "" };
  const bookedKey = toStayDateTime(booked.date, booked.time, DEFAULT_ARRIVAL_TIME);
  const original = fmtDateAndTime(booked.date, booked.time);

  let earliest = null;
  if (STATUSES_WITH_ASSIGNED_ROOM.includes(b.status)) {
    for (const a of b.roomAssignments || []) {
      if (a.isNoShow) continue;
      let start;
      if (a.actualCheckin && !planned) {
        const d = new Date(a.actualCheckin);
        if (Number.isNaN(d.getTime())) continue;
        start = { date: localDateKey(d), time: localTimeKey(d), actual: true };
      } else {
        start = {
          date: a.effectiveArrival || booked.date,
          time: a.effectiveArrivalTime || booked.time,
          actual: false,
        };
      }
      start.key = toStayDateTime(start.date, start.time, DEFAULT_ARRIVAL_TIME);
      if (start.key && (!earliest || start.key < earliest.key)) earliest = start;
    }
  }

  if (!earliest) return { ...booked, key: bookedKey, changed: false, original };
  const changed = earliest.actual ? earliest.date !== booked.date : earliest.key !== bookedKey;
  return { date: earliest.date, time: earliest.time, key: earliest.key, changed, original };
}

/* Earliest departure first, by the same value the Departure column shows. A
   row with no date sorts last and ties break on id, as for arrivals. */
export function compareByDeparture(a, b) {
  const ka = bookingDeparture(a).key || NO_ARRIVAL;
  const kb = bookingDeparture(b).key || NO_ARRIVAL;
  if (ka !== kb) return ka < kb ? -1 : 1;
  return (a?.id ?? 0) - (b?.id ?? 0);
}

export const stayNights = (ci, co) => {
  if (!ci || !co) return "—";
  if (ci === co) return "day use";
  const n = Math.round((new Date(co) - new Date(ci)) / 86400000);
  return n > 0 ? `${n} night${n > 1 ? "s" : ""}` : "—";
};

/* Does a stay occupy this calendar day? Checkout day is normally excluded
   (the room frees up that morning), but a day-use stay arrives and departs
   on the same date and must still claim it — otherwise it covers no days at
   all and reads as though the room were free. */
export const coversDate = (checkIn, checkOut, dateKey) => {
  if (!checkIn || !checkOut) return false;
  if (checkIn === checkOut) return dateKey === checkIn;
  return dateKey >= checkIn && dateKey < checkOut;
};

export const val = (v) =>
  v !== undefined && v !== null && v !== "" ? String(v) : "—";

/* Caps a long cell value so one over-long name can't stretch a whole column.
   Callers should put the untruncated text in the cell's `title` so hovering
   still gives the full value — nothing is ever only visible truncated. */
export const truncate = (text, max = 20) => {
  const s = String(text ?? "");
  return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
};

/* Lower-cased with every punctuation mark dropped, so "A.K. Sharma",
   "a.k.sharma" and "A K-Sharma" all fold toward "ak sharma"/"aksharma". */
export const normalizeSearchText = (text) =>
  String(text ?? "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "");

/* The haystack a search box matches against: the text as typed plus its
   punctuation-free fold, so a date like "2026-09-08" still matches itself and
   "ak sharma" still finds "A.k.sharma". */
export function buildSearchHaystack(parts) {
  const raw = parts.filter(Boolean).join(" ").toLowerCase();
  return `${raw} ${normalizeSearchText(raw)}`;
}

/* True when every whitespace-separated term of `query` appears in the
   haystack, either as typed or with its punctuation dropped. */
export function matchesSearch(haystack, query) {
  const terms = String(query ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((t) => {
    if (haystack.includes(t)) return true;
    const folded = normalizeSearchText(t);
    return Boolean(folded) && haystack.includes(folded);
  });
}

/* Every guest on a booking: the lead guest plus each room's named occupant,
   de-duplicated case-insensitively in first-seen order. */
export function allGuestNames(booking) {
  const seen = new Set();
  const names = [];
  for (const n of [booking.guestName, ...(booking.roomAssignments || []).map((a) => a.guestName)]) {
    const name = String(n ?? "").trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

/* The compact booking-type label for narrow table columns. */
export function shortBookingType(booking) {
  if (booking.bookingTypeCode === "OFFICIAL") return "Official";
  if (booking.bookingTypeCode === "NON_OFFICIAL" || booking.bookingTypeCode === "PERSONAL") return "N-Offi";
  return booking.bookingType || "";
}

/* Supplementary display-only duration for a completed stay: elapsed time from
   actual check-in to actual check-out, in 24-hour blocks — e.g. 15 Aug 11:00 ->
   16 Aug 15:00 is 28 hrs -> 2 "day" blocks. Applied per room assignment, since
   rooms on one booking can have quite different stays. Returns null unless both
   stamps are present, so a room still occupied or never arrived shows nothing
   rather than a provisional figure. Deliberately separate from
   stayNights/num_days, which billing relies on and which this must never
   influence. */
export function hourBlockDuration(actualCheckin, actualCheckout) {
  if (!actualCheckin || !actualCheckout) return null;
  const start = new Date(actualCheckin);
  const end = new Date(actualCheckout);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return null;
  }
  const hours = (end - start) / 3600000;
  return { hours: Math.round(hours * 10) / 10, days: Math.ceil(hours / 24) };
}

/* created_at comes back as a full ISO timestamp (e.g. 2026-06-22T10:30:00Z),
   unlike the plain YYYY-MM-DD dates fmt()/fmtDate() expect — show date + time. */
export function fmtDateTime(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return `${ddmmyyyy(date)}, ${hhmmA(date.getHours(), date.getMinutes())}`;
}

/* The booking's most recent transfer, or null. The API leaves transfers
   unordered, so this picks by transferred_at itself. */
export function latestTransfer(booking) {
  const transfers = booking?.transfers || [];
  if (!transfers.length) return null;
  return transfers.reduce((latest, t) =>
    (t.transferred_at || "") > (latest.transferred_at || "") ? t : latest,
  );
}

/* Transfer-related status-history rows aren't a status change — hide them
   from the Status History trail (the field carrying the old status doesn't
   reliably say "TRANSFERRED", so match on the backend's own remark text). */
export function isTransferHistoryEntry(h) {
  if (h.new_status === "TRANSFERRED") return true;
  return (h.remarks || "").toLowerCase().includes("transferred to guest house");
}

/* Shared background for columns marked `meta: { highlight: true }` in a
   DataTable column definition — see DataTable.js. */
export const HIGHLIGHT_COLUMN_COLOR = "#fff3cd";

/* Actual check-in/check-out across all room assignments (multi-room bookings
   can have different times per room) — earliest in, latest out. `checkout`
   is only reported once every room that has checked in has also checked out
   (otherwise it's a partial checkout and callers should treat it as not yet
   complete). Shared by BookingDetailModal (duration calc) and SummaryTab
   (Checked In/Out columns). */
export function getActualCheckInOut(booking) {
  const checkins = (booking.roomAssignments || []).map((a) => a.actualCheckin).filter(Boolean);
  const checkouts = (booking.roomAssignments || []).map((a) => a.actualCheckout).filter(Boolean);
  return {
    checkin: checkins.length ? checkins.sort()[0] : null,
    checkout: checkouts.length === checkins.length && checkouts.length ? checkouts.sort().slice(-1)[0] : null,
  };
}

/* Room-map status colors — shared between RoomMapTab, RoomReservationsModal
   and the per-room chips below. Semantic UI named colors, applied via each
   consumer's `color` prop. */
export const STATUS_COLOR = {
  Vacant: "blue",
  Reserved: "yellow",
  Occupied: "green",
  Maintenance: "red",
};

/* Booking statuses wear the room-map palette above, each taking the color of
   the room state it implies — a Checked In booking is the same green as the
   Occupied room it holds on the map, an Approved one the same yellow as that
   room while it's only Reserved. Pending holds no room at all, so it stays
   neutral grey rather than borrowing a room color. Drives both the status
   badge (StatusBadge.js) and the row tint below, so the two can't drift. */
export const BOOKING_STATUS_COLOR = {
  Pending:               "grey",
  Approved:              STATUS_COLOR.Reserved,
  "Partially Confirmed": STATUS_COLOR.Reserved,
  "Checked In":          STATUS_COLOR.Occupied,
  "Checked Out":         STATUS_COLOR.Vacant,
  Transferred:           STATUS_COLOR.Vacant,
  Rejected:              STATUS_COLOR.Maintenance,
  Cancelled:             STATUS_COLOR.Maintenance,
  "No Show":             STATUS_COLOR.Maintenance,
};

/* Pale wash of each Semantic UI named color, for tinting a table row without
   fighting the status Label sitting inside it. Keyed by the color names
   BOOKING_STATUS_COLOR yields, so a row always matches its own badge. */
export const ROW_TINT = {
  grey:   "#f4f4f5",
  yellow: "#fff8e1",
  green:  "#e9f7ee",
  blue:   "#e8f1fb",
  red:    "#fdeced",
};

/* Only the two statuses still waiting on someone get a row tint — everything
   else stays white so the rows needing action are the ones that stand out. */
const TINTED_STATUSES = ["Pending", "Approved"];

export const getRowStyle = (booking) => {
  if (!TINTED_STATUSES.includes(booking.status)) return undefined;
  const backgroundColor = ROW_TINT[BOOKING_STATUS_COLOR[booking.status]];
  return backgroundColor ? { backgroundColor } : undefined;
};

/* The booking tables' row style: the status tint, in bold while no admin has
   opened the indent yet. */
export const bookingRowStyle = (booking) => {
  const style = getRowStyle(booking);
  return booking.isUnread ? { ...style, fontWeight: 700 } : style;
};

/* One room assignment's own state, which is not the booking's state: a booking
   flips to Checked In the moment its *first* room is checked in (see `checkin`
   in bookings/views.py), so the rest can still be waiting on a guest. Anything
   showing rooms individually has to ask this rather than read booking.status.

   Order matters and mirrors the server's: a room marked no-show is a no-show
   even if it was checked in and out first, and a checkout supersedes the
   check-in that preceded it.

   Colors come from the room-map palette above, so a room chip matches the
   booking badge of the same meaning — a checked-in room is the same green as
   the Occupied room it holds on the map, one still awaiting its guest the same
   yellow as that room while it's only Reserved. A room the guest has already
   left carries no color at all: it needs nothing from the desk, so it should
   recede rather than compete with the rooms that do. */
export function roomOccupancyState(a) {
  if (a?.isNoShow) {
    return { key: "noShow", text: "No Show", color: BOOKING_STATUS_COLOR["No Show"] };
  }
  if (a?.actualCheckout) {
    return { key: "checkedOut", text: "Checked Out", color: undefined };
  }
  if (a?.actualCheckin) {
    return { key: "checkedIn", text: "Checked In", color: STATUS_COLOR.Occupied };
  }
  return { key: "awaiting", text: "Awaiting Check-In", color: STATUS_COLOR.Reserved };
}

/* How many of a booking's rooms are in, out, awaiting or no-show — the "2 of 3
   rooms checked in" a multi-room booking needs to be readable at a glance.

   Gated on STATUSES_WITH_ASSIGNED_ROOM for the same reason the Room No column
   is: a Cancelled booking keeps its assignment rows as audit history, and
   counting those would report rooms it no longer holds.

   `arrived` is how many rooms are past the awaiting stage at all (checked in,
   already out, or written off as a no-show) — callers use it to tell a booking
   whose arrivals have started from one that hasn't begun, where "0 of 3 in"
   would be noise rather than news. */
export function roomOccupancyCounts(booking) {
  const counts = { total: 0, checkedIn: 0, checkedOut: 0, awaiting: 0, noShow: 0, arrived: 0 };
  if (!STATUSES_WITH_ASSIGNED_ROOM.includes(booking?.status)) return counts;
  for (const a of booking.roomAssignments || []) {
    counts.total += 1;
    counts[roomOccupancyState(a).key] += 1;
  }
  counts.arrived = counts.checkedIn + counts.checkedOut + counts.noShow;
  return counts;
}

/* Booking statuses that actually hold a room. Mirrors the backend list in
   bookings/views.py `_check_availability` and guest-houses `available-rooms`
   — keep the two in step. Anything else (notably CANCELLED, which keeps its
   assignment rows as audit history) leaves its rooms free to reassign. */
export const HOLDING_STATUSES = ["CONFIRMED", "PARTIALLY_CONFIRMED", "CHECKED_IN"];

/* Minutes remaining until a timed auto-maintenance hold clears, or null
   once it's already expired / not timed (e.g. a manual hold). */
export const minutesLeft = (untilIso) => {
  if (!untilIso) return null;
  const mins = Math.ceil((new Date(untilIso).getTime() - Date.now()) / 60000);
  return mins > 0 ? mins : null;
};

/* Seeds the admin Create Booking form from an existing indent ("Reuse
   Details"). Returns only the keys it fills, so the caller can spread it over
   the form's EMPTY_CB defaults and let everything else stay blank.

   Deliberately absent: arrival/departure date + time (a reused indent is for a
   new visit, and copying old dates would only trip the past-date validation),
   `preferences` / `room_requests_by_preference` (deciding those afresh is the
   whole point of raising a new indent), and `official_attachment` (the form
   holds a File, the booking only a stored URL — it can't be reconstructed). */
export const bookingToCreateForm = (booking) => {
  const b = booking || {};
  return {
    employee_id:                b.employeeId            || "",
    applicant_name:             b.applicantName         || "",
    designation:                b.applicantDesignation  || "",
    department:                 b.applicantDepartment   || "",
    email:                      b.emailId               || "",
    mobile:                     b.applicantMobileNumber || "",
    guest_name:                 b.guestName             || "",
    guest_designation:          b.guestDesignation      || "",
    guest_mobile:               b.guestMobileNumber     || "",
    guest_address:              b.guestAddress          || "",
    booking_type:               b.bookingTypeCode       || "OFFICIAL",
    purpose_of_visit:           b.visitPurpose          || "",
    payment_responsibility:     b.paymentResponsibility || "",
    payment_responsibility_other: b.paymentResponsibilityOther || "",
    accompanying_persons_count: Number(b.accompanyingPersonsCount) || 0,
    rooms_required:             Number(b.roomCount) || 1,
  };
};
